/**
 * Diálogo de detalle de registro de auditoría de ventas home
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  Security,
  AccessTime,
  Person,
  Category,
  TouchApp,
  Http,
  Language,
  Link as LinkIcon,
  Fingerprint,
  QrCode,
  CompareArrows,
  Upload,
  Download,
  Devices,
  RemoveCircleOutline,
  AddCircleOutline,
} from '@mui/icons-material';

import {
  useFetchAuditoriaDetalle,
  formatFechaAuditoria,
  getAccionColor,
  getModuloColor,
  getMetodoHttpColor,
} from '@/shared/actions/erp/auditoria-ventas-home.actions';

/** Formatea un valor de cambio para mostrarlo legible */
const formatCambioValue = (val: any): string => {
  if (val === undefined || val === null) return 'N/A';
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

/** Campos _data son objetos anidados redundantes, se filtran */
const CAMPOS_DATA_EXCLUIDOS = /_data$|^coordenadas_array$/;

interface Props {
  logId: string;
  open: boolean;
  onClose: () => void;
}

export const AuditoriaVentasHomeDetalle = ({ logId, open, onClose }: Props) => {
  const theme = useTheme();
  const { data: log, isLoading } = useFetchAuditoriaDetalle(logId, open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header con gradiente */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
              }}
            >
              <Security sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                Detalle de Auditoría
              </Typography>
              {log && (
                <Typography variant="caption" color="text.secondary">
                  {log.modulo} - {log.accion}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.1) },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: alpha(theme.palette.primary.main, 0.01), py: 3 }}>
        {isLoading ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" p={6}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Cargando detalle...
            </Typography>
          </Box>
        ) : log ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Información General */}
            <Paper
              elevation={1}
              sx={{
                p: 0,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                <Typography variant="subtitle2" fontWeight={700} display="flex" alignItems="center" gap={1}>
                  <Category fontSize="small" color="primary" />
                  Información General
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14 }} /> Fecha y Hora (Ecuador)
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                        {log.fecha_hora_local || formatFechaAuditoria(log.fecha_hora)}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <Person sx={{ fontSize: 14 }} /> Usuario
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {log.usuario_nombre || 'Sistema'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <Category sx={{ fontSize: 14 }} /> Módulo
                      </Typography>
                      <Chip label={log.modulo} size="small" color={getModuloColor(log.modulo)} variant="outlined" sx={{ fontWeight: 600 }} />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <TouchApp sx={{ fontSize: 14 }} /> Acción
                      </Typography>
                      <Chip label={log.accion} size="small" color={getAccionColor(log.accion)} sx={{ fontWeight: 600 }} />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <Http sx={{ fontSize: 14 }} /> Método HTTP
                      </Typography>
                      <Chip
                        label={log.metodo_http || '-'}
                        size="small"
                        color={getMetodoHttpColor(log.metodo_http || '')}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <Language sx={{ fontSize: 14 }} /> IP Address
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                        {log.ip_address || '-'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        Estado HTTP
                      </Typography>
                      <Chip
                        label={log.estado_http || '-'}
                        size="small"
                        color={log.estado_http && log.estado_http >= 200 && log.estado_http < 300 ? 'success' : 'error'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                        <LinkIcon sx={{ fontSize: 14 }} /> Endpoint
                      </Typography>
                      <Box
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          bgcolor: alpha(theme.palette.text.primary, 0.04),
                          p: 1.5,
                          borderRadius: 1.5,
                          border: `1px solid ${theme.palette.divider}`,
                          fontWeight: 500,
                        }}
                      >
                        {log.endpoint || '-'}
                      </Box>
                    </Box>
                  </Grid>

                  {log.registro_id && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                            <Fingerprint sx={{ fontSize: 14 }} /> Registro ID
                          </Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 500 }}>
                            {log.registro_id}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                            <QrCode sx={{ fontSize: 14 }} /> Código
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>{log.registro_codigo || '-'}</Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Paper>

            {/* Cambios Realizados */}
            {log.cambios && Object.keys(log.cambios).length > 0 && (
              <Paper
                elevation={1}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  }}
              >
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
                  <Typography variant="subtitle2" fontWeight={700} display="flex" alignItems="center" gap={1}>
                    <CompareArrows fontSize="small" color="warning" />
                    Cambios Realizados
                    <Chip
                      label={Object.entries(log.cambios).filter(([c]) => !CAMPOS_DATA_EXCLUIDOS.test(c)).length}
                      size="small"
                      color="warning"
                      sx={{ ml: 1, height: 22, fontWeight: 700, fontSize: '0.7rem' }}
                    />
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ maxHeight: 450, overflow: 'auto', p: 2 }}>
                  {Object.entries(log.cambios)
                    .filter(([campo]) => !CAMPOS_DATA_EXCLUIDOS.test(campo))
                    .map(([campo, valor]: [string, any]) => (
                    <Box
                      key={campo}
                      sx={{
                        mb: 2,
                        '&:last-child': { mb: 0 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{
                          color: theme.palette.primary.main,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          display: 'inline-block',
                          mb: 1,
                          fontSize: '0.72rem',
                        }}
                      >
                        {campo}
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: alpha(theme.palette.error.main, 0.03),
                              borderColor: alpha(theme.palette.error.main, 0.2),
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="error.main"
                              fontWeight={600}
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{ mb: 0.5 }}
                            >
                              <RemoveCircleOutline sx={{ fontSize: 14 }} /> Anterior
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.72rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                              }}
                            >
                              {formatCambioValue(valor?.anterior)}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: alpha(theme.palette.success.main, 0.03),
                              borderColor: alpha(theme.palette.success.main, 0.2),
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="success.main"
                              fontWeight={600}
                              display="flex"
                              alignItems="center"
                              gap={0.5}
                              sx={{ mb: 0.5 }}
                            >
                              <AddCircleOutline sx={{ fontSize: 14 }} /> Nuevo
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.72rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.primary',
                              }}
                            >
                              {formatCambioValue(valor?.nuevo)}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Request Body */}
            {log.request_body && Object.keys(log.request_body).length > 0 && (
              <Paper
                elevation={1}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  }}
              >
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(theme.palette.info.main, 0.04) }}>
                  <Typography variant="subtitle2" fontWeight={700} display="flex" alignItems="center" gap={1}>
                    <Upload fontSize="small" color="info" />
                    Payload (Request)
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ maxHeight: 350, overflow: 'auto', p: 2, bgcolor: alpha(theme.palette.text.primary, 0.01) }}>
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(log.request_body, null, 2)}
                  </pre>
                </Box>
              </Paper>
            )}

            {/* Response Body */}
            {log.response_body && Object.keys(log.response_body).length > 0 && (
              <Paper
                elevation={1}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: alpha(
                      log.estado_http && log.estado_http >= 200 && log.estado_http < 300
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                      0.04
                    ),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} display="flex" alignItems="center" gap={1}>
                    <Download fontSize="small" color={log.estado_http && log.estado_http >= 200 && log.estado_http < 300 ? 'success' : 'error'} />
                    Response
                  </Typography>
                  <Chip
                    label={log.estado_http || '-'}
                    size="small"
                    color={log.estado_http && log.estado_http >= 200 && log.estado_http < 300 ? 'success' : 'error'}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
                <Divider />
                <Box sx={{ maxHeight: 350, overflow: 'auto', p: 2, bgcolor: alpha(theme.palette.text.primary, 0.01) }}>
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(log.response_body, null, 2)}
                  </pre>
                </Box>
              </Paper>
            )}

            {/* User Agent */}
            {log.user_agent && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                  <Devices sx={{ fontSize: 14 }} /> User Agent
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.68rem',
                    color: 'text.secondary',
                    lineHeight: 1.6,
                  }}
                >
                  {log.user_agent}
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            No se pudo cargar el registro
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2, px: 3 }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditoriaVentasHomeDetalle;
